'use client'
import { BackToTopButton } from 'components/BackToTopButton'
import { Container } from 'components/Container'
import { Overline } from 'components/Overline'
import { Link, Locale } from 'i18n/routing'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import mapIllustration from 'public/illustrations/footer-map.svg'
import LinkedIn from 'components/icons/LinkedIn'
import Instagram from 'components/icons/Instragram'
import { FooterBlockQuery } from '.generated/dato'

type Props = {
  locale: Locale
  content: FooterBlockQuery
}

export function FooterClient({ locale, content }: Props) {
  const today = new Date()
  const t = useTranslations()

  return (
    <>
      <footer className="bg-black text-white highlight-orange">
        <Container width="narrow" className="py-64 flex flex-col gap-48">
          <svg className="w-48 h-40">
            <title>farbenmeer Logo</title>
            <use href="/logo-icon.svg#logo" />
          </svg>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-32 lg:gap-68 justify-between">
            {/* left column */}
            <div className="inline-flex flex-col gap-44">
              <figure>
                <span className="w-0 grow max-w-384">
                  <Link href="/kontakt">
                    <Image
                      src={mapIllustration}
                      alt={t('footer.mapToOurOffice')}
                    />
                  </Link>
                </span>
              </figure>
            </div>

            {/* middle column */}
            <div className="inline-flex flex-col gap-44">
              <section className="flex flex-col gap-16">
                <Overline small>{t('footer.address')}</Overline>
                <address className="leading-30 not-italic flex flex-col gap-8">
                  <span>Neuer Kamp 30</span>
                  <span>20357 Hamburg</span>
                </address>
              </section>

              <section className="flex flex-col gap-16">
                <Overline small>{t('footer.aboutUs')}</Overline>
                <ul className="lg:leading-26 flex flex-col gap-8">
                  <li>
                    <Link
                      href="/ueber-uns/arbeitsweise"
                      className="hover:underline"
                    >
                      {t('footer.ourOperation')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/ueber-uns/engagement"
                      className="hover:underline"
                    >
                      {t('footer.engagement')}
                    </Link>
                  </li>
                  <li>
                    <Link href="/jobs" className="hover:underline">
                      {t('footer.jobs')}
                    </Link>
                  </li>
                </ul>
              </section>

              <section className="flex flex-col gap-16">
                <Overline small>{t('footer.legal')}</Overline>
                <ul className="flex flex-col gap-8">
                  <li>
                    <Link href="/datenschutz" className="hover:underline">
                      {t('nav.privacy')}
                    </Link>
                  </li>
                  <li>
                    <Link href="/impressum" className="hover:underline">
                      {t('nav.legal')}
                    </Link>
                  </li>
                </ul>
              </section>
            </div>

            {/* right column */}
            <div className="inline-flex flex-col gap-44">
              <section className="flex flex-col gap-16">
                <Overline small>{t('footer.conceptAndDesign')}</Overline>
                <ul className="flex flex-col gap-8">
                  {content.allSkillPages.map((skill) => (
                    <li key={skill.id}>
                      <Link
                        href={{
                          pathname: '/kompetenzen/[slug]',
                          params: { slug: skill.slug },
                        }}
                        className="hover:underline"
                      >
                        {skill.heading}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="flex flex-col gap-16">
                <Overline small>{t('nav.contact')}</Overline>
                <ul>
                  <li>
                    <Link href="/kontakt/projekt" className="hover:underline">
                      {t('footer.projectRequest')}
                    </Link>
                  </li>
                </ul>
                <figure>
                  <address className="not-italic relative">
                    <ul className="leading-33 md:leading-26 whitespace-nowrap">
                      <li>
                        <a
                          href={`mailto:moin@farbenmeer.de`}
                          aria-label={t('common.emailUsAt', {
                            emailAddress: 'moin@farbenmeer.de',
                          })}
                        >
                          moin@farbenmeer.de
                        </a>
                      </li>
                    </ul>
                    <svg
                      width="342"
                      height="66"
                      className="md:hidden absolute -top-8 pointer-events-none left-[-0.75rem] right-[-0.75rem] text-violet"
                      aria-hidden
                    >
                      <use href="/scribbles/lets-talk.svg#lets-talk" />
                    </svg>

                    <svg
                      width="351"
                      height="87"
                      className="hidden md:block absolute -top-24 -left-112 pointer-events-none text-violet"
                      aria-hidden
                    >
                      <use href="/scribbles/lets-talk-left.svg#lets-talk-left" />
                    </svg>
                  </address>
                </figure>
              </section>

              <section className="flex flex-col gap-16">
                <Overline small>{t('footer.followUs')}</Overline>
                <ul className="leading-33 md:leading-26 flex gap-40">
                  <li>
                    <a
                      role="button"
                      href="https://www.linkedin.com/company/farbenmeer"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex rounded-full bg-white text-black p-8 hover:bg-orange hover:text-white"
                      aria-label="LinkedIn"
                    >
                      <LinkedIn className="w-24 h-24 inline-block" />
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.instagram.com/farbenmeer_gmbh/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex rounded-full bg-white text-black p-8 hover:bg-orange hover:text-white"
                      aria-label="Instagram"
                    >
                      <Instagram className="w-24 h-24 inline-block" />
                    </a>
                  </li>
                </ul>
              </section>
            </div>
          </div>
          <Overline className="text-lightGray" small>
            © {today.getFullYear()} farbenmeer GmbH
          </Overline>
        </Container>
      </footer>
      <BackToTopButton />
    </>
  )
}
